import { isDefined, isNotDefined } from '@/utils/assertions';
import { formatDate, formatDateTime } from '@/utils/date';
import type { ValueFormatterFunc } from 'ag-grid-community';

type FieldTypology = 'amount' | 'longAmount' | 'percentage' | 'percentageNoDigitLimit' | 'date' | 'dateTime' | 'text'

export function getAgGridFormatters<PositionType>(_locale: string) {
  const locale = 'fr'; // expressively asked by product owner

  const percentage2digitsFormatter = new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 2,
  })

  const percentageNodigitsLimitFormatter = new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  })

  const amountFormatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });

  const longAmountFormatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  });

  const agGridFormatters: { [typology in FieldTypology]: ValueFormatterFunc<PositionType> } = {
    amount: ({ value }) => (isDefined(value) ? amountFormatter.format(value) : '-'),
    longAmount: ({value}) => (isDefined(value) ? longAmountFormatter.format(value) : '-'),
    percentage: ({ value }) => (isDefined(value) ? percentage2digitsFormatter.format(value / 100) : '-'),
    percentageNoDigitLimit: ({ value }) => (isDefined(value) ? percentageNodigitsLimitFormatter.format(value / 100) : '-'),
    date: ({ value }) => (isDefined(value) && value !== '' ? formatDate(value) : '-'),
    dateTime: ({ value }) => (isDefined(value) && value !== '' ? formatDateTime(value) : '-'),
    text: ({ value }) => value || '-',
  };

  return agGridFormatters;
}

export function getFixedNumberOfDecimalsFormatterWithCurrency<T>(currencyExtractor: (_: T) => string ) :ValueFormatterFunc<T> {
  return ({value, data}) => {
    if(isNotDefined(data)) return '-'
    const curr = currencyExtractor.apply(currencyExtractor, [data]);
    if (isDefined(curr) && isDefined(value)) {
      const f = getFixedNumberOfDecimalsFormatterForCurrency<T>(curr)
      return f.apply(f, [value])
    }
    return '-'
  }
}

export function getFixedNumberOfDecimalsFormatters<T>(nb: number) : ValueFormatterFunc<T>{
  const locale = 'fr'
  const nDecimalsFormatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: nb,
    maximumFractionDigits: nb,
  })

  return ({ value }) => (isDefined(value) && value !== '' ? nDecimalsFormatter.format(value) : '-')
}

function getFixedNumberOfDecimalsFormatterForCurrency<T>(currency: string) : ValueFormatterFunc<T>{
  switch(currency) {
    case 'JPY':
      return getFixedNumberOfDecimalsFormatters(0)
    case 'TND':
      return getFixedNumberOfDecimalsFormatters(3)
    default:
      return getFixedNumberOfDecimalsFormatters(2)
  }
} 
